import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ConnectionService } from '../../service/connection.service';
import { SwalService } from '../../service/swal.service';
import { TourService } from '../../service/tour.service';
import { AuthService } from '../../service/auth.service';
import { ThemeService, ThemePreset } from '../../service/theme.service';
import { SubscriptionService } from '../../service/subscription.service';
import { environment } from '../../../environments/environment';
import { IconComponent } from '../../components/icon/icon.component';
import { LoadErrorComponent } from '../../components/load-error/load-error.component';

@Component({
  selector: 'app-profilo',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, TranslatePipe, IconComponent, LoadErrorComponent],
  templateUrl: './profilo.component.html',
  styleUrls: ['./profilo.component.css'],
})
export class ProfiloComponent implements OnInit {
  user: any;

  activeTab = 0;
  tabs = ['profile.tabs.profile', 'profile.tabs.team', 'profile.tabs.appearance'];

  themePresets: ThemePreset[] = [];

  number_of_weddings = 0;
  number_of_contracts = 0;


  profileForm!: FormGroup;

  isEditOpen = false;

  currentVersion = environment.appVersion;

  readonly tourTabIds = ['tour-tab-profilo', 'tour-tab-team', 'tour-tab-aspetto'];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private connection: ConnectionService,
    private swal: SwalService,
    private cdr: ChangeDetectorRef,
    private tour: TourService,
    private auth: AuthService,
    private themeService: ThemeService,
    private subscription: SubscriptionService,
    private route: ActivatedRoute,
  ) {
    this.themePresets = this.themeService.presets;
  }

  ngOnInit() {
    if (this.route.snapshot.queryParamMap.get('tab') === 'team') this.setTab(1);
    this.tour.profilo();
    this.subscription.loadEntitlements();
    this.load();
  }

  // Profilo non arrivato: il modulo resta nascosto, salvarlo vuoto cancellerebbe i dati veri
  loadError = false;
  teamMembersError = false;

  load() {
    this.connection.get(
      'auth/profile',
      (data: any) => {
        this.loadError = false;
        this.user = data;
        this.initForms(data);
        this.loadStats();
      },
      () => (this.loadError = true),
    );
  }

  private initForms(data: any) {
    const u = data.user;

    this.selectedCategories = u.professional_categories ?? [];

    this.profileForm = this.fb.group({
      name: [u.name, Validators.required],
      lastname: [u.lastname, Validators.required],
      username: [u.username, Validators.required],
      email: [u.email, [Validators.required, Validators.email]],
      organization: [u.organization],
      operating_area: [u.operating_area ?? ''],
      hair: [u.service?.hair ?? false],
      makeup: [u.service?.makeup ?? false],
      photo: [u.service?.photo ?? false],
      current_password: [''],
      new_password: ['', Validators.minLength(6)],
      confirm_password: [''],
    });


    if (u.avatar && !u.avatar.startsWith('http')) {
      this.user.user.avatar = `${environment.apiUrl}/auth/avatar/${data._id}?t=${Date.now()}`;
    }
  }
  private loadStats() {
    this.connection.get(
      'events/number-of-weddings',
      (data: number) => (this.number_of_weddings = data),
      () => {},
    );
    this.connection.get(
      'events/number-of-contracted-weddings',
      (data: number) => (this.number_of_contracts = data),
      () => {},
    );
  }

  // ── PROFILO ───────────────────────────────────────────────────────────────

  readonly CATEGORIES = [
    { value: 'makeup', label: 'Makeup Artist' },
    { value: 'hair', label: 'Hair Stylist' },
    { value: 'photo', label: 'Fotografo/a' },
    { value: 'video', label: 'Videomaker' },
    { value: 'dj', label: 'DJ' },
    { value: 'band', label: 'Musica Live' },
    { value: 'florist', label: 'Fiorista' },
    { value: 'other', label: 'Altro' },
  ];

  selectedCategories: string[] = [];

  toggleCategory(value: string) {
    const idx = this.selectedCategories.indexOf(value);
    if (idx >= 0) this.selectedCategories.splice(idx, 1);
    else this.selectedCategories.push(value);
  }

  hasCategory(value: string): boolean {
    return this.selectedCategories.includes(value);
  }

  get attivity(): string {
    if (this.selectedCategories.length) {
      return this.selectedCategories
        .map(v => this.CATEGORIES.find(c => c.value === v)?.label ?? v)
        .join(', ');
    }
    return 'Professionista wedding';
  }

  saveProfile() {
    if (this.profileForm.invalid) {
      this.swal.errorDialog('Errore', 'Correggi i campi obbligatori');
      return;
    }

    const v = this.profileForm.value;

    // Verifica password
    if (v.new_password) {
      if (v.new_password !== v.confirm_password) {
        this.swal.errorDialog('Errore', 'Le password non coincidono');
        return;
      }
    }

    const payload: any = {
      'user.name': v.name,
      'user.lastname': v.lastname,
      'user.email': v.email,
      'user.organization': v.organization,
      'user.operating_area': v.operating_area,
      'user.professional_categories': this.selectedCategories,
      'user.service.hair': v.hair,
      'user.service.makeup': v.makeup,
      'user.service.photo': v.photo,
    };

    if (v.new_password) {
      payload['user.password'] = v.new_password;
    }

    this.connection.put(
      'auth/update',
      payload,
      (data: any) => {
        this.user = data;
        this.isEditOpen = false;
        localStorage.setItem('profile_completed', 'true');
        this.swal.success('Profilo aggiornato!');
        this.cdr.detectChanges();
      },
      () => this.swal.errorDialog('Errore', 'Salvataggio fallito'),
    );
  }

  // ── ASPETTO (tema/palette) ──────────────────────────────────────────────────

  get canProAppearance(): boolean {
    return this.subscription.canPro();
  }

  get activeThemeId(): string {
    return this.themeService.activeTheme;
  }

  selectTheme(preset: ThemePreset): void {
    if (
      preset.isPro &&
      !this.subscription.requirePro(
        'Le palette a tema sono disponibili con il piano Pro o Agency.',
      )
    ) {
      return;
    }
    this.themeService.setTheme(preset.id);
  }

  themeSwatchGradient(preset: ThemePreset): string {
    return `linear-gradient(90deg, ${preset.swatch.gold}, ${preset.swatch.rose})`;
  }

  // ── AUTH ──────────────────────────────────────────────────────────────────

  logout() {
    // Revoca la tessera lato server (best-effort), poi pulisci e vai al login.
    this.auth.logout().subscribe(() => {
      localStorage.removeItem('token');
      if ((window as any).$crisp) {
        (window as any).$crisp.push(['do', 'chat:hide']);
        (window as any).$crisp.push(['do', 'session:reset']);
      }
      this.router.navigate(['/login']);
    });
  }

  // ── PROFILE ───────────────────────────────────────────────────────────────

  uploadAvatar(event: any) {
    const file = event.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('avatar', file);
    this.connection.post(
      'auth/upload-avatar',
      formData,
      () => {
        this.user.user.avatar = `${environment.apiUrl}/auth/avatar/${this.user._id}?t=${Date.now()}`;
        this.swal.success('Avatar aggiornato!');
        this.cdr.detectChanges();
      },
      () => this.swal.errorDialog('Errore', 'Upload avatar fallito'),
    );
  }

  // ── TEAM (accessi Nyzed) ───────────────────────────────────────────────────
  teamMembers: any[] = [];
  teamMembersLoaded = false;
  newTeamEmail = '';

  setTab(i: number) {
    this.activeTab = i;
    if (i === 1 && !this.teamMembersLoaded) this.loadTeamMembers();
  }

  loadTeamMembers() {
    this.connection.get(
      '@auth/team/members',
      (data: any) => {
        this.teamMembers = data.members ?? [];
        this.teamMembersLoaded = true;
        this.teamMembersError = false;
        this.cdr.detectChanges();
      },
      () => {
        this.teamMembersLoaded = true;
        this.teamMembersError = true;
        this.cdr.detectChanges();
      },
    );
  }

  inviteTeamMember() {
    const email = this.newTeamEmail.trim();
    if (!email) {
      this.swal.error('Inserisci una email');
      return;
    }
    this.connection.post(
      'auth/team/invite',
      { email },
      () => {
        this.swal.success('Invito inviato');
        this.newTeamEmail = '';
        this.loadTeamMembers();
      },
      (err: any) => {
        if (err?.status === 403) return; // gestito dall'errorInterceptor (piani)
        this.swal.error(err?.error?.message ?? 'Invio invito fallito');
      },
    );
  }

  resendTeamInvite(member: any) {
    this.connection.post(
      'auth/team/invite',
      { email: member.user?.email },
      () => this.swal.success('Invito reinviato'),
      (err: any) => {
        if (err?.status === 403) return; // gestito dall'errorInterceptor (piani)
        this.swal.error(err?.error?.message ?? 'Errore reinvio');
      },
    );
  }

  removeTeamMemberAccess(member: any) {
    this.swal
      .confirm(
        'Rimuovi accesso',
        'Il membro non potrà più accedere ai matrimoni assegnati.',
      )
      .then((result) => {
        if (result.isConfirmed) {
          this.connection.delete(
            `auth/team/members/${member._id}`,
            () => {
              this.swal.success('Accesso rimosso');
              this.loadTeamMembers();
            },
            () => this.swal.error('Rimozione fallita'),
          );
        }
      });
  }

  teamMemberLabel(member: any): string {
    const name = `${member.user?.name ?? ''} ${member.user?.lastname ?? ''}`.trim();
    return name || member.user?.email || '—';
  }
}
